<template>
	<div>
		<div class="kpi-grid">
			<div class="kpi-card">
				<div class="kpi-label">Period</div>
				<div class="kpi-value">
					{{ kpis?.period_name || '—' }}
					<PTag
						v-if="kpis?.period_status"
						:severity="statusSeverity(kpis.period_status)"
						class="ml-2"
					>{{ kpis.period_status }}</PTag>
					<PTag v-if="kpis?.is_trial" class="ml-1" severity="warning">trial</PTag>
				</div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">NEEn,act</div>
				<div class="kpi-value">{{ fmt(kpis?.nee_n_act) }} <span class="kpi-unit">kWh</span></div>
				<div v-if="kpis?.prev_nee_n_act != null" class="kpi-delta">
					vs {{ kpis.prev_period_name }}: {{ fmt(kpis.prev_nee_n_act) }}
				</div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">NEEn,deemed</div>
				<div class="kpi-value">{{ fmt(kpis?.nee_n_deemed) }} <span class="kpi-unit">kWh</span></div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">Deemed − actual gap</div>
				<div class="kpi-value" :class="gapClass">
					{{ fmt(kpis?.nee_gap_kwh) }}
					<span class="kpi-unit">kWh</span>
					<span v-if="kpis?.nee_gap_pct != null" class="kpi-unit">
						({{ fmtSigned(kpis.nee_gap_pct) }}%)
					</span>
				</div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">TPn</div>
				<div class="kpi-value">{{ fmt(kpis?.tpn) }} <span class="kpi-unit">SAR</span></div>
				<div v-if="kpis?.prev_tpn != null" class="kpi-delta">
					vs {{ kpis.prev_period_name }}: {{ fmt(kpis.prev_tpn) }}
				</div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">Invoice total (incl. VAT)</div>
				<div class="kpi-value">{{ fmt(kpis?.invoice_total) }} <span class="kpi-unit">SAR</span></div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">Avg PR / APR / PPR</div>
				<div class="kpi-value text-base">
					{{ fmtPr(kpis?.avg_pr) }}
					<span class="kpi-unit">/</span>
					{{ fmtPr(kpis?.avg_apr) }}
					<span class="kpi-unit">/</span>
					{{ fmtPr(kpis?.avg_ppr) }}
				</div>
			</div>
			<div class="kpi-card">
				<div class="kpi-label">Valid / invalid hours</div>
				<div class="kpi-value">
					<span class="text-green-500">{{ kpis?.valid_hours ?? '—' }}</span>
					<span class="text-color-secondary"> / </span>
					<span class="text-orange-500">{{ kpis?.invalid_hours ?? '—' }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'
	import { DashboardKpis } from '@/brokers/DashboardBroker'

	export default defineComponent({
		name: 'DashKpiStrip',
		props: {
			kpis: { type: Object as PropType<DashboardKpis | null>, default: null },
		},
		computed: {
			gapClass(): string {
				const g = this.kpis?.nee_gap_pct
				if (g == null) return ''
				if (Math.abs(g) > 10) return 'text-orange-500'
				return ''
			},
		},
		methods: {
			fmt(v: number | null | undefined): string {
				if (v == null || Number.isNaN(v)) return '—'
				return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })
			},
			fmtSigned(v: number | null | undefined): string {
				if (v == null || Number.isNaN(v)) return '—'
				const n = Number(v)
				const s = n.toLocaleString(undefined, { maximumFractionDigits: 2 })
				return n > 0 ? `+${s}` : s
			},
			fmtPr(v: number | null | undefined): string {
				if (v == null || Number.isNaN(v)) return '—'
				return Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 })
			},
			statusSeverity(status: string): string {
				const s = (status || '').toLowerCase()
				if (s === 'open') return 'info'
				if (s === 'finalized') return 'warning'
				if (s === 'invoiced') return 'success'
				return 'secondary'
			},
		},
	})
</script>

<style scoped>
	.kpi-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
		gap: 0.75rem;
	}
	.kpi-card {
		border: 1px solid var(--surface-border);
		border-radius: 6px;
		padding: 0.75rem 1rem;
		background: var(--surface-ground);
	}
	.kpi-label {
		font-size: 0.75rem;
		color: var(--text-color-secondary);
		margin-bottom: 0.25rem;
	}
	.kpi-value {
		font-size: 1.05rem;
		font-weight: 600;
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.15rem;
	}
	.kpi-unit {
		font-size: 0.75rem;
		font-weight: 500;
		color: var(--text-color-secondary);
		margin-left: 0.25rem;
	}
	.kpi-delta {
		margin-top: 0.2rem;
		font-size: 0.7rem;
		color: var(--text-color-secondary);
	}
</style>
